import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Header,
  Dropdown,
  Icon,
  Label,
  Button,
  Modal,
} from "semantic-ui-react";

const Post = ({ item, canEdit, onEdit, onDelete, onPin }) => {
  const [mediaError, setMediaError] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [truncatedText, setTruncatedText] = useState("");
  const [fullText, setFullText] = useState("");
  const textRef = useRef(null);
  const mediaRef = useRef(null);

  // Character-based truncation logic
  useEffect(() => {
    if (item.description) {
      // Strip HTML tags for accurate character counting
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = item.description;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      setFullText(item.description);

      if (plainText.length > 30) {
        setShowToggle(true);
        // Find the last complete word within 30 characters
        const truncated = plainText.substring(0, 30);
        const lastSpaceIndex = truncated.lastIndexOf(" ");
        const finalTruncated =
          lastSpaceIndex > 0
            ? truncated.substring(0, lastSpaceIndex)
            : truncated;

        // Create truncated HTML by finding the position in original HTML
        const truncatedHtml = createTruncatedHtml(
          item.description,
          finalTruncated.length
        );
        setTruncatedText(truncatedHtml);
      } else {
        setShowToggle(false);
        setTruncatedText(item.description);
      }
    }
  }, [item.description]);

  // Helper function to create truncated HTML while preserving tags
  const createTruncatedHtml = (html, maxLength) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    let charCount = 0;
    let result = "";
    let openTags = [];

    const processNode = (node) => {
      if (charCount >= maxLength) return false;

      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = maxLength - charCount;
        if (node.textContent.length <= remaining) {
          result += node.textContent;
          charCount += node.textContent.length;
        } else {
          result += node.textContent.substring(0, remaining);
          charCount = maxLength;
          return false;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        result += `<${tagName}`;

        // Add attributes
        for (let attr of node.attributes) {
          result += ` ${attr.name}="${attr.value}"`;
        }
        result += ">";

        openTags.push(tagName);

        // Process child nodes
        for (let child of node.childNodes) {
          if (!processNode(child)) break;
        }

        result += `</${tagName}>`;
        openTags.pop();
      }

      return charCount < maxLength;
    };

    for (let child of tempDiv.childNodes) {
      if (!processNode(child)) break;
    }

    // Close any remaining open tags
    while (openTags.length > 0) {
      result += `</${openTags.pop()}>`;
    }

    return result;
  };

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    if (!mediaRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const media = entry.target;
          const dataSrc = media.getAttribute("data-src");

          if (dataSrc && !media.src) {
            media.src = dataSrc;
            if (media.tagName === "VIDEO") {
              media.load();
            }
          }
          observer.unobserve(media);
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(mediaRef.current);

    return () => {
      if (mediaRef.current) observer.unobserve(mediaRef.current);
    };
  }, [mediaRef.current, item.id]); // Added item.id to re-trigger when post changes

  const getImageUrl = (item) => {
    const possibleUrls = [
      item.imageUrl,
      item.image_url,
      item.url,
      item.image,
      item.imagePath,
      item.image_path,
      item.attachmentUrl,
      item.attachment_url,
    ];
    const url = possibleUrls.find((url) => url && url.trim() !== "") || null;
    return url;
  };

  const isVideo = (url) => /\.(mp4|webm|ogg)$/i.test(url);
  const isImage = (url) => /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(url);

  const isPinned = item.isPinned || item.is_pinned || false;
  const mediaUrl = getImageUrl(item);

  const handleMediaLoad = () => {
    setMediaLoading(false);
    setMediaError(false);
  };

  const handleMediaError = (e) => {
    setMediaLoading(false);
    setMediaError(true);
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const openDeleteModal = () => setDeleteModalOpen(true);
  const closeDeleteModal = () => setDeleteModalOpen(false);
  const confirmDelete = () => {
    onDelete(item.id);
    closeDeleteModal();
  };

  // Reset media loading state when item or mediaUrl changes
  useEffect(() => {
    if (mediaUrl) {
      setMediaLoading(true);
      setMediaError(false);

      // Force image to load by creating a new Image object
      if (isImage(mediaUrl)) {
        const img = new Image();
        img.onload = () => {
          setMediaLoading(false);
          setMediaError(false);
        };
        img.onerror = () => {
          setMediaLoading(false);
          setMediaError(true);
        };
        img.src = mediaUrl;
      }
    }
  }, [item.id, mediaUrl]);

  return (
    <>
      <Card
        fluid
        raised
        style={{
          borderRadius: 10,
          border: isPinned ? "2px solid #f4b952" : "1px solid #ddd",
          boxShadow: isPinned
            ? "0 4px 12px rgba(24, 103, 43, 0.15)"
            : "0 1px 3px rgba(0,0,0,0.2)",
          padding: "1em",
          marginBottom: "1.5em",
          background: "#ffffffff",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1em",
            maxWidth: "100%",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ flex: 1 }}>
              <Header as="h4" style={{ margin: 0 }}>
                {item.title}
                {isPinned && (
                  <Label
                    color="green"
                    size="mini"
                    style={{
                      marginLeft: "0.5em",
                      fontWeight: "bold",
                      verticalAlign: "middle",
                    }}
                  >
                    <Icon name="pin" /> Pinned
                  </Label>
                )}
              </Header>
              <p
                style={{ color: "#555", fontSize: "0.9em", marginTop: "0.3em" }}
              >
                {item.fullName} • {item.date}
              </p>
            </div>

            {canEdit && (
              <Dropdown
                icon="ellipsis horizontal"
                floating
                className="icon"
                direction="left"
                style={{ backgroundColor: "#ffffffff", boxShadow: "none" }}
              >
                <Dropdown.Menu>
                  <Dropdown.Item
                    icon="pin"
                    text={isPinned ? "Unpin" : "Pin"}
                    onClick={() => onPin(item.id)}
                    style={{
                      color: isPinned ? "#f4b952" : "#21ba45",
                      fontWeight: isPinned ? "normal" : "600",
                    }}
                  />
                  <Dropdown.Divider />
                  <Dropdown.Item
                    icon="edit"
                    text="Edit"
                    onClick={() => onEdit(item)}
                  />
                  <Dropdown.Item
                    icon="trash"
                    text="Delete"
                    onClick={openDeleteModal}
                    style={{ color: "red" }}
                  />
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>

          {/* Description */}
          <div
            ref={textRef}
            style={{
              fontSize: "1em",
              color: "#3a3838",
              wordWrap: "break-word",
              lineHeight: "1.6em",
            }}
            dangerouslySetInnerHTML={{
              __html: isExpanded ? fullText : truncatedText,
            }}
          />
          {showToggle && (
            <Button
              onClick={toggleExpanded}
              style={{
                padding: 0,
                background: "none",
                color: "#1b1c1d",
                fontSize: "0.85em",
                cursor: "pointer",
                alignSelf: "flex-start",
              }}
            >
              {isExpanded ? (
                <>
                  See less <Icon name="chevron up" />
                </>
              ) : (
                <>
                  See more... <Icon name="chevron down" />
                </>
              )}
            </Button>
          )}

          {/* Media */}
          {mediaUrl && (isExpanded || !showToggle) && (
            <div
              style={{
                width: "100%",
                borderRadius: 6,
                overflow: "hidden",
                marginTop: "0.5em",
              }}
            >
              {mediaLoading && !mediaError && (
                <div
                  style={{ textAlign: "center", padding: "1em", color: "#888" }}
                >
                  <Icon name="spinner" loading /> Loading media...
                </div>
              )}

              {!mediaError && isImage(mediaUrl) && (
                <img
                  key={`${item.id}-${mediaUrl}`}
                  data-src={mediaUrl}
                  src={mediaUrl}
                  loading="lazy"
                  ref={mediaRef}
                  alt="Post Media"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "400px",
                    objectFit: "cover",
                    display: mediaLoading ? "none" : "block",
                  }}
                  onLoad={handleMediaLoad}
                  onError={handleMediaError}
                />
              )}

              {!mediaError && isVideo(mediaUrl) && (
                <video
                  data-src={mediaUrl}
                  src={mediaUrl}
                  ref={mediaRef}
                  preload="metadata"
                  controls
                  style={{
                    width: "100%",
                    maxHeight: "480px",
                    display: mediaLoading ? "none" : "block",
                  }}
                  onLoadedData={handleMediaLoad}
                  onError={handleMediaError}
                />
              )}

              {mediaError && (
                <div
                  style={{
                    padding: "1em",
                    textAlign: "center",
                    border: "2px dashed #ccc",
                    borderRadius: "6px",
                    backgroundColor: "#fafafa",
                    color: "#777",
                  }}
                >
                  <Icon name="exclamation triangle" />
                  <p>Media could not be loaded.</p>
                  <small style={{ wordBreak: "break-word" }}>
                    URL: {mediaUrl}
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        size="tiny"
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        dimmer="blurring"
      >
        <Modal.Header>Delete Announcement</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to delete the announcement{" "}
            <strong>{item.title}</strong>?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button positive onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

Post.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    postedBy: PropTypes.string,
    fullName: PropTypes.string,
    date: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    image_url: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.string,
    imagePath: PropTypes.string,
    image_path: PropTypes.string,
    attachmentUrl: PropTypes.string,
    attachment_url: PropTypes.string,
    isPinned: PropTypes.bool,
    is_pinned: PropTypes.bool,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
};

export default Post;
